import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import Icona from './Icone';
import { useTraduzioni } from '../../i18n';
import { API, axios } from '../../utils/api';
import { dispositivoDiQuestoBrowser, requestNotificationPermission, statoNotifiche, subscribeToPush } from '../../utils/serviceWorker';

// La card «Attiva le notifiche» della home del cliente. Il permesso non si chiede MAI da solo: si chiede qui,
// dopo il login, quando il cliente tocca il bottone e ha letto a cosa servono. I browser lo chiedono una volta
// sola: se si nega, si rimette solo dalle impostazioni del telefono, e la card lo spiega.
//
// Su iPhone (iOS 16.4 e successivi) le notifiche arrivano SOLO se l'app è stata aggiunta alla schermata Home:
// fuori di lì la card non mostra il bottone ma i due passi da fare (Condividi → «Aggiungi a Home»).
//
// Testi: le chiavi home.notifiche.* del dizionario. Finché non ci sono, vale il testo italiano scritto qui
// (RISERVA): mai una chiave grezza a video.
const RISERVA = {
  titolo: 'Attiva le notifiche',
  testo: 'Vi avvisiamo quando fissiamo una visita, quando un documento è a posto e quando c\'è una novità sulla vostra casa. Niente pubblicità.',
  attiva: 'Attiva le notifiche',
  attivazione: 'Attivazione…',
  non_ora: 'Non ora',
  attivate: 'Notifiche attive su questo dispositivo.',
  errore: 'Non siamo riusciti ad attivarle: riprovate tra poco.',
  iphone_titolo: 'Sull\'iPhone le notifiche arrivano dall\'app',
  iphone_1: 'In Safari toccate Condividi (il quadrato con la freccia in su).',
  iphone_2: 'Scegliete «Aggiungi a Home» e aprite MK Client da lì.',
  iphone_3: 'Nell\'app tornate su questa card e toccate «Attiva le notifiche».',
  negato_titolo: 'Le notifiche sono bloccate',
  negato_testo: 'Il permesso si chiede una volta sola. Per rimetterlo:',
  negato_iphone: 'Impostazioni del telefono › Notifiche › MK Client › Consenti notifiche.',
  negato_browser: 'Nel browser: lucchetto accanto all\'indirizzo › Notifiche › Consenti, poi ricaricate la pagina.',
  riattiva_titolo: 'Riattivate le notifiche',
  riattiva_testo: 'Su questo dispositivo le notifiche non arrivano più: bastano due secondi per rimetterle.',
  riattiva: 'Riattiva le notifiche',
};

const CHIAVE_NON_ORA = 'mk_notifiche_non_ora';   // fino a quando la card non si ripropone (ms)
const TRENTA_GIORNI = 30 * 24 * 60 * 60 * 1000;

const rimandata = () => {
  try { return Number(localStorage.getItem(CHIAVE_NON_ORA) || 0) > Date.now(); } catch { return false; }
};
const rimanda = () => {
  try { localStorage.setItem(CHIAVE_NON_ORA, String(Date.now() + TRENTA_GIORNI)); } catch { /* archivio non disponibile */ }
};

// Il testo tradotto; se la chiave non c'è ancora nel dizionario, quello italiano qui sopra
export const testoNotifiche = (t, chiave) => {
  const completa = `home.notifiche.${chiave}`;
  const tradotto = t(completa);
  return tradotto === completa ? RISERVA[chiave] : tradotto;
};

// Le notifiche risultano accese ma il server non riesce più a mandarle a questo dispositivo (per esempio le
// chiavi VAPID sono cambiate: il servizio push rifiuta l'iscrizione vecchia). Senza questa card il cliente non
// vedrebbe niente e resterebbe muto per sempre: era il bug delle push che non arrivavano (docs/notifiche.md).
const CardNotifiche = ({ stato: statoIniziale = null, daRiattivare = null }) => {
  const { t } = useTraduzioni();
  const [stato, setStato] = useState(() => statoIniziale || statoNotifiche());
  // il server sa se a QUESTO dispositivo le notifiche non arrivano più (backend/push_service.py): si chiede una
  // volta sola, e solo quando il permesso c'è già (se no la risposta non cambierebbe niente)
  const [guasto, setGuasto] = useState(false);
  const [attesa, setAttesa] = useState(false);
  const [nascosta, setNascosta] = useState(() => rimandata());
  const testo = useCallback((chiave) => testoNotifiche(t, chiave), [t]);

  useEffect(() => {
    if (daRiattivare !== null || stato.stato !== 'attive') return undefined;
    let vivo = true;
    axios.get(`${API}/push/dispositivi`)
      .then((r) => {
        const mio = dispositivoDiQuestoBrowser();
        const righe = Array.isArray(r.data) ? r.data : [];
        const questo = mio?.id ? righe.find((d) => d.id === mio.id) : null;
        if (vivo) setGuasto(Boolean(questo?.da_riattivare));
      })
      .catch(() => { /* senza risposta la card resta com'era */ });
    return () => { vivo = false; };
  }, [daRiattivare, stato.stato]);

  // Tornando nell'app (per esempio dopo averla aggiunta alla schermata Home) lo stato può essere cambiato
  useEffect(() => {
    const guarda = () => setStato(statoNotifiche());
    document.addEventListener('visibilitychange', guarda);
    return () => document.removeEventListener('visibilitychange', guarda);
  }, []);

  const attiva = async () => {
    setAttesa(true);
    try {
      const concesso = await requestNotificationPermission();
      if (!concesso) {
        setStato(statoNotifiche());
        return;
      }
      const iscrizione = await subscribeToPush();
      setStato(statoNotifiche());
      if (iscrizione) toast.success(testo('attivate'));
      else toast.error(testo('errore'));   // permesso dato ma iscrizione impossibile (iPhone fuori dall'app)
    } catch {
      toast.error(testo('errore'));
    } finally {
      setAttesa(false);
    }
  };

  const nonOra = () => {
    rimanda();
    setNascosta(true);
  };

  const riattivare = (daRiattivare === null ? guasto : daRiattivare) && stato.stato === 'attive';
  if ((stato.stato === 'attive' && !riattivare) || stato.stato === 'non_supportate') return null;
  if (nascosta && stato.stato !== 'negato' && !riattivare) return null;   // «non ora» non nasconde un guasto

  const bloccate = stato.stato === 'negato';
  const daInstallare = stato.stato === 'da_installare';

  return (
    <div className="search r" data-testid="card-notifiche" data-stato={stato.stato}>
      <div className="items">
        <div className="it">
          <div className="i"><Icona nome="campanella" /></div>
          <div>
            <b style={{ fontSize: '1.1rem' }}>
              {testo(riattivare ? 'riattiva_titolo' : bloccate ? 'negato_titolo' : daInstallare ? 'iphone_titolo' : 'titolo')}
            </b>
            <span>{testo(riattivare ? 'riattiva_testo' : bloccate ? 'negato_testo' : 'testo')}</span>
            {bloccate && (
              <span data-testid="notifiche-come-sbloccare">{testo(stato.iphone ? 'negato_iphone' : 'negato_browser')}</span>
            )}
            {daInstallare && (
              <span data-testid="notifiche-passi-iphone">
                {`1. ${testo('iphone_1')} 2. ${testo('iphone_2')} 3. ${testo('iphone_3')}`}
              </span>
            )}
          </div>
        </div>
      </div>
      {!bloccate && !daInstallare && (
        <>
          <button type="button" className="btn sm p" onClick={attiva} disabled={attesa} data-testid="notifiche-attiva">
            {testo(attesa ? 'attivazione' : riattivare ? 'riattiva' : 'attiva')}
          </button>
          {/* un guasto non si rimanda di trenta giorni: o si riattiva, o resta lì */}
          {!riattivare && (
            <button type="button" className="btn sm" style={{ marginLeft: 0 }} onClick={nonOra} data-testid="notifiche-non-ora">
              {testo('non_ora')}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default CardNotifiche;
